import { observer } from 'mobx-react';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { LoadingButton } from '@mui/lab';
import useIntegrityAdvocate from 'components/IntegrityAdvocate/hooks/useIntegrityAdvocate';
import { Form, Formik } from 'formik';
import { useGATracking } from 'hooks/useGATracking';
import useNotification from 'hooks/useNotification';
import { DateTime } from 'luxon';
import getChunksInitialValues from 'modules/Programs/utils/getChunksInitialValues';
import store from 'store';

import { submitGrades } from '@common/api/grades';
import { sendLessonAnalitics } from '@common/api/students';
import { checkRole } from '@common/components/RBAC';
import { ServicesIds } from '@common/components/ServiceRouter';
import { LESSON_HOMEWORK_END_TIME } from '@common/constants/GaEvents';
import gradebookStatusId from '@common/constants/gradebookStatusId';
import { appId } from '@common/constants/integrityadvocateserver';
import { IChunksModel } from '@common/models/Chunks/Chunks';
import { IStudentLessonModel } from '@common/models/StudentLessons/Studentlesson';

import Topic from './Topic';
import StyledLessonChunksWrap from './style';

interface PropsType {
	chunksModel: IChunksModel;
	lessonModel: IStudentLessonModel;
	canBeForm: boolean;
}

function Chunks({ chunksModel, lessonModel, canBeForm }: PropsType) {
	const { t } = useTranslation();
	const { programId, groupId, programGroupId, scheduleLessonId } = useParams();
	const lessonEndTime = DateTime.local().toFormat('yyyy-MM-dd HH:mm:ss');
	const { sendEvent } = useGATracking();

	const [loading, setLoading] = useState(false);
	const [isSubmitted, setIsSubmitted] = useState(false);
	const notificate = useNotification();
	const { endSession } = useIntegrityAdvocate(
		appId,
		lessonModel?.schedule_program?.id,
		store?.profile?.student?.id,
		'1',
	);

	if (chunksModel.data?.length <= 0) return null;

	const initialValues = useMemo(
		() => getChunksInitialValues(chunksModel, lessonModel, canBeForm),
		[],
	);

	const hasFormValues = Object.entries(initialValues).length > 0;

	const isDone =
		lessonModel.gradebook.is_done === gradebookStatusId.done ||
		lessonModel.gradebook.is_done === gradebookStatusId.onChecking ||
		isSubmitted;

	return (
		<Formik
			onSubmit={async (data, { setStatus }) => {
				setLoading(true);
				try {
					const chunks: any[] = Object.values(data);
					await submitGrades(
						chunks.map((chunk) => {
							const studentFileArr = chunk.student_file?.filter((item: any) => {
								if (Array.isArray(item)) {
									return !!item.length;
								}
								return !!item;
							});
							const studentUrlArr = studentFileArr?.map((item: any) => item.url);
							const dictaphoneUrlArr = chunk.dictaphone_url?.filter(Boolean);

							return {
								...chunk,
								student_file: studentFileArr,
								student_url: studentUrlArr,
								dictaphone_url: dictaphoneUrlArr,
							};
						}),
					);
					notificate('success', t('lesson.complete'));
					setIsSubmitted(true);
					endSession();
					setStatus({ success: true });
					if (store.services.current.id === ServicesIds.TALENTED || checkRole('Partner')) {
						await store.modalNotifications.fetch({
							service_id: store.services.current.id,
							is_modal: 1,
						});
						await store.profile.fetch();
					}
					sendEvent(LESSON_HOMEWORK_END_TIME, {
						end_time: lessonEndTime,
						user_id: store.profile.user_id,
						student_id: store?.currentStudentId,
						program_id: programId,
						group_id: groupId,
						program_group_id: programGroupId,
						schedule_lesson_id: scheduleLessonId,
					});

					if (!checkRole('Parent')) {
						await sendLessonAnalitics({
							name: 'completion',
							schedule_lesson_id: Number(scheduleLessonId),
							end_time: lessonEndTime,
						});
					}
				} catch (e) {
					if (e?.errors?.[0]) {
						notificate('error', e.errors[0]);
					} else {
						notificate('warning', t('lesson.errorComplete'), { withoutTitle: true });
					}
					console.error(e);
				} finally {
					setLoading(false);
				}
			}}
			initialValues={initialValues}
		>
			<Form>
				<StyledLessonChunksWrap>
					{Object.values(chunksModel.mapByTopic).map((topic) => (
						<Topic
							key={topic.topic.id}
							topic={topic}
							gradeBook={lessonModel.gradebook}
							disabled={isDone}
						/>
					))}
					{hasFormValues && (
						<LoadingButton
							loading={loading}
							type="submit"
							variant="contained"
							sx={{
								marginLeft: 'auto',
								width: 'fit-content',
								fontSize: '13px',
								padding: '10px 16px',
								height: 'auto',
							}}
							disabled={isDone || store.profile.viewOnlyMode}
						>
							{isDone ? t('shared.complete') : t('shared.submit')}
						</LoadingButton>
					)}
				</StyledLessonChunksWrap>
			</Form>
		</Formik>
	);
}

export default observer(Chunks);
